import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { AlertCircle } from "lucide-react";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";

/**
 * Wraps the PayPal SDK with graceful fallback for:
 *  - Missing client-id (env var empty)
 *  - Invalid client-id (PayPal SDK script fails to load with HTTP 4xx)
 * Prevents the page from crashing when PayPal can't initialize.
 */
export default function PayPalCheckout({
  createOrder,
  onApprove,
  onCancel,
  onError,
  amount,
  currency = "USD",
}) {
  const [sdkFailed, setSdkFailed] = useState(false);

  // Pre-flight check: if SDK script can't load, mark as failed instead of
  // letting react-paypal-js throw an uncaught error.
  useEffect(() => {
    if (!PAYPAL_CLIENT_ID) return;
    let cancelled = false;
    fetch(
      `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(
        PAYPAL_CLIENT_ID
      )}&currency=${currency}&intent=capture`,
      { method: "HEAD" }
    )
      .then((res) => {
        if (!cancelled && !res.ok) {
          console.warn("PayPal SDK validation failed:", res.status);
          setSdkFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setSdkFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [currency]);

  if (!PAYPAL_CLIENT_ID || sdkFailed) {
    return (
      <div
        className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-2"
        data-testid="paypal-unavailable"
      >
        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold mb-1">PayPal temporarily unavailable</p>
          <p className="text-amber-800">
            PayPal checkout isn't configured yet. Please use{" "}
            <strong>Pay with Card (Stripe)</strong> above, or contact us to pay another way.
          </p>
        </div>
      </div>
    );
  }

  return (
    <PayPalScriptProvider
      options={{
        clientId: PAYPAL_CLIENT_ID,
        currency,
        intent: "capture",
        components: "buttons",
      }}
    >
      <PayPalButtons
        style={{ layout: "horizontal", height: 55, tagline: false }}
        createOrder={createOrder}
        onApprove={onApprove}
        onError={(err) => {
          console.error("PayPal error:", err);
          setSdkFailed(true);
          if (onError) onError(err);
        }}
        onCancel={onCancel}
      />
      {amount !== undefined && (
        <p className="text-xs text-slate-500 text-center mt-2" data-testid="paypal-amount-note">
          Charged in USD ≈ {currency} {Number(amount).toFixed(2)}
        </p>
      )}
    </PayPalScriptProvider>
  );
}
